import React from 'react';
import styled from 'styled-components';
import { Formik, Form as FormikForm } from 'formik';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import format from 'date-fns/format';
import isPast from 'date-fns/is_past';
import {
  FileInput as FileInputBase,
  DropdownSelect,
  DateInput,
  Button,
  Color,
} from '@figure/frontlib-gui';
import { usePersonalLoan } from 'shared/hooks';
import { FormErrorHandler, Typography } from 'shared/components';
import Stack from 'shared/components/Stack';
import { UploadedFilePreview as UploadedFilePreviewBase } from 'shared/components/UploadedFilePreview';

const Container = styled.div`
  padding: 2rem;
  border: 1px solid ${Color.GREY_3};
`;

const FileInput = styled(FileInputBase)`
  margin-bottom: 20px;
  span {
    overflow-wrap: anywhere;
  }
`;

const Form = styled(FormikForm)`
  & > :nth-last-child(2) {
    margin-bottom: 36px;
  }
`;

const UploadedFilePreview = styled(UploadedFilePreviewBase)`
  margin-bottom: 1rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
`;

const PAY_FREQUENCIES = [
  { label: 'Weekly', value: 'WEEKLY' },
  { label: 'Biweekly', value: 'BIWEEKLY' },
  { label: 'Semimonthly', value: 'SEMIMONTHLY' },
  { label: 'Monthly', value: 'MONTH' },
];

const DOCUMENT_TYPES = {
  paystub: 'USER_UPLOADED_PAYSTUB',
};

const headerTextMap = {
  paystub: 'Use your most recent paystub for verification',
};

const getLatestUploadedPaystubDocumentUuid = (documents) => {
  const paystubUploads = documents.filter((doc) => doc.type === DOCUMENT_TYPES.paystub);
  return paystubUploads[paystubUploads.length - 1].uuid;
};

const PaystubUpload = ({ type, personalLoanUuid, setSuccessfulSubmit, onBack }) => {
  const { uploadDocument, saveManualPaystubData } = usePersonalLoan();

  const handleOnSubmit = async ({ payFrequency, payDate, paystubFile }) => {
    const formattedPayDate = format(payDate, 'YYYY-MM-DD');

    const documents = await uploadDocument({
      file: paystubFile,
      type: DOCUMENT_TYPES.paystub,
      uuid: personalLoanUuid,
    }).then(({ documents }) => documents);

    await saveManualPaystubData({
      payFrequency,
      payDate: formattedPayDate,
      paystubDocUuid: getLatestUploadedPaystubDocumentUuid(documents),
      plUuid: personalLoanUuid,
    });

    setSuccessfulSubmit(true);
  };

  return (
    <>
      <Typography.PageHeader>{headerTextMap[type]}</Typography.PageHeader>
      <Stack space="1.6rem">
        <Typography.Body>
          Upload your most recent paystub and confirm details to help expedite processing. Please
          note that your paystub must contain regular pay. If the paystub supplied is not your most
          recent, you will not be able to use this method. Contact support if you have questions
          about the eligibility of your paystub.
        </Typography.Body>
        <Typography.Body>
          The quickest way to verify your income is to connect a source using our automated options.
          Press ‘back’ below to return to the previous page and connect an additional income source.
        </Typography.Body>
        <Container>
          <Formik
            initialValues={{
              payFrequency: '',
              payDate: '',
              paystubFile: null,
            }}
            validationSchema={() =>
              yup.object().shape({
                payFrequency: yup.string().required(),
                payDate: yup
                  .string()
                  .length(10, 'Date must be formatted XX/XX/XXXX')
                  .test('pay-date-past', 'Date must be in the past', (value) => isPast(value))
                  .required(),
              })
            }
            onSubmit={handleOnSubmit}
          >
            {({ values, touched, errors, setFieldValue, handleSubmit }) => (
              <>
                <FormErrorHandler errors={errors} touched={touched} />
                <Form onSubmit={handleSubmit} noValidate={true}>
                  <UploadedFilePreview file={values?.paystubFile} />
                  <FileInput
                    required
                    id="paystubFile"
                    name="paystubFile"
                    label={values.paystubFile ? values.paystubFile.name : 'Upload Paystub'}
                    errorText={touched.paystubFile && errors.paystubFile}
                    accept="image/png, image/jpeg, application/pdf"
                    onChange={(e) => {
                      const { files } = e.target;
                      setFieldValue('paystubFile', files[0]);
                    }}
                    file={{
                      value: values.paystubFile,
                      complete: !!values.paystubFile,
                      progress: values.paystubFile ? 100 : 0,
                    }}
                    onRemove={() => {
                      setFieldValue('paystubFile', null);
                    }}
                  />

                  <DropdownSelect
                    required
                    id="pay-frequency"
                    data-test-id="pay-frequency"
                    name="pay-frequency"
                    label="Pay Frequency"
                    value={values.payFrequency}
                    errorText={touched.payFrequency && errors.payFrequency}
                    onChange={(e) => setFieldValue('payFrequency', e.target.value)}
                  >
                    <option value="" disabled />
                    {PAY_FREQUENCIES.map((f) => (
                      <option key={`${f.label}`} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </DropdownSelect>

                  <DateInput
                    required
                    id="pay-date"
                    data-test-id="pay-date"
                    name="pay-date"
                    label="Pay Date"
                    value={values.payDate}
                    errorText={touched.payDate && errors.payDate}
                    onChange={(v) => setFieldValue('payDate', v)}
                  />

                  <ButtonGroup>
                    <Button type="button" onClick={onBack}>
                      Back
                    </Button>
                    <Button primary type="submit">
                      Upload and continue
                    </Button>
                  </ButtonGroup>
                </Form>
              </>
            )}
          </Formik>
        </Container>
      </Stack>
    </>
  );
};

PaystubUpload.propTypes = {
  type: PropTypes.string.isRequired,
  personalLoanUuid: PropTypes.string.isRequired,
  setSuccessfulSubmit: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default PaystubUpload;
